<!-- C:\VUE3\platform\src\components\common\AppLauncher.vue -->
<template>
  <!-- 네비게이션에 바로 꽂아 쓰는 드롭다운 -->
  <li class="nav-item dropdown" data-bs-display="static">
    <!-- ✅ SEO: 고유 ID, aria-haspopup 추가 -->
    <button
      :id="buttonId"
      class="nav-link btn px-2"
      type="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      aria-haspopup="true"
      :aria-label="ariaLabel"
    >
      <i class="bi bi-grid-3x3-gap fs-5" aria-hidden="true"></i>
    </button>

    <!-- ✅ SEO: aria-labelledby로 버튼과 연결, role="menu" 추가 -->
    <div
      class="dropdown-menu dropdown-menu-end p-3 shadow-lg app-launcher-menu"
      :aria-labelledby="buttonId"
      role="menu"
    >
      <div class="row row-cols-3 g-2" role="group" aria-label="앱 목록">
        <div v-for="it in items" :key="it.id" class="col">
          <!-- ✅ SEO: role="menuitem", 동적 aria-label 추가 -->
          <button
            type="button"
            class="btn app-tile w-100 d-flex flex-column align-items-center justify-content-center py-3"
            role="menuitem"
            :aria-label="`${it.label}${it.requiresAuth ? ' (로그인 필요)' : ''}`"
            @click="open(it)"
          >
            <i :class="['app-icon mb-2 fs-3', it.icon]" aria-hidden="true"></i>
            <small class="app-label text-truncate">{{ it.label }}</small>
          </button>
        </div>
      </div>

      <div v-if="moreLink || $slots.footer" class="border-top mt-3 pt-3">
        <!-- ✅ SEO: 새 창 안내 추가 -->
        <a
          v-if="moreLink"
          :href="moreLink"
          class="btn btn-light w-100"
          target="_blank"
          rel="noopener noreferrer"
          role="menuitem"
        >
          모든 앱 보기
          <span class="visually-hidden">(새 창에서 열림)</span>
          <i class="bi bi-box-arrow-up-right ms-1 small" aria-hidden="true"></i>
        </a>
        <slot name="footer"></slot>
      </div>
    </div>
  </li>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useMainStore } from "@/store/mainStore";
import { logError } from "@/utils/logHelper";

const props = defineProps({
  items: { type: Array, default: () => [] },
  moreLink: { type: String, default: "" },
  ariaLabel: { type: String, default: "앱 런처 메뉴 열기" },
});

const emit = defineEmits(["select"]);

const router = useRouter();
const mainStore = useMainStore();

// ✅ SEO: 고유 ID 생성 (다중 인스턴스 대응)
const buttonId = computed(
  () => `app-launcher-btn-${Math.random().toString(36).slice(2, 9)}`,
);

function openLink(href, target = "_blank") {
  try {
    window.open(href, target, "noopener");
  } catch (e) {
    logError("AppLauncher openLink failed:", e);
  }
}

function open(it) {
  try {
    emit("select", it);

    // 라우트 이동
    if (it.route) {
      // 보호 라우트 처리
      if (it.requiresAuth && !mainStore.isLogin) {
        const nextPath = router.resolve(it.route).fullPath;
        router.push({ path: "/login", query: { next: nextPath } });
        return;
      }
      router.push(it.route);
      return;
    }

    // 외부 링크
    if (it.href) {
      openLink(it.href, it.target || "_self");
    }
  } catch (e) {
    logError("AppLauncher open failed:", e);
  }
}
</script>

<style scoped>
.app-launcher-menu {
  width: 350px;
  max-width: 90vw;
  animation: fadeIn 0.2s ease-out;
}

.app-tile {
  border: 1px solid transparent;
  background-color: transparent;
  transition: all 0.2s ease-in-out;
  border-radius: 0.75rem;
  color: #2c3033;
}

.app-tile:hover,
.app-tile:focus {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* ✅ SEO: 포커스 표시 강화 (키보드 접근성) */
.app-tile:focus-visible {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
}

.app-label {
  font-size: 0.875rem;
}

/* ✅ SEO: 스크린리더 전용 텍스트 */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
